import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  findPatternMatches,
  normalizeApiCandles,
  normalizeCloses,
  summarizeMatches,
} from './patternMath';
import { useLang } from '../../i18n/context';
import RebateBanner from '../../components/RebateBanner';
import useUserStore from '../../store/userStore';
import Swal from 'sweetalert2';
import { ROUTES } from '../../routes/routeConfig';
import './PatternMatchPage.css';

const FIXED_MATCH_COUNT = 5;

const DEFAULT_PARAMS = {
  patternLength: 20,
  forwardWindow: 10,
  exclusionWindow: 20,
};
// 形态长度 / 后续观察：10/20 直接按钮；30/40/50 收进下拉，登录后可用
const BASIC_OPTIONS = [10, 20];
const PREMIUM_OPTIONS = [30, 40, 50];
const HISTORY_LIMIT = 3000;
const DEFAULT_SYMBOL = 'BTCUSDT';
const DEFAULT_INTERVAL = '1h';
const SYMBOL_OPTIONS = [
  { value: 'BTCUSDT', label: 'BTC/USDT' },
  { value: 'ETHUSDT', label: 'ETH/USDT' },
  { value: 'SOLUSDT', label: 'SOL/USDT' },
  { value: 'PAXGUSDT', label: 'PAXG/USDT' },
];
const INTERVAL_OPTIONS = [
  { value: '1h', label: '1H' },
  { value: '4h', label: '4H' },
  { value: '1d', label: '1D' },
];

const TOP_COLORS = ['#1f6fff', '#ff8a1f', '#20b96b', '#c9952e', '#ec4899'];

function buildFallbackCandles(interval = DEFAULT_INTERVAL) {
  const intervalSeconds = interval === '1d' ? 86400 : interval === '4h' ? 14400 : 3600;
  const start = 1781836800;
  return Array.from({ length: 120 }, (_, index) => {
    const trend = index * 0.7;
    const wave = Math.sin(index / 5) * 4 + Math.cos(index / 11) * 2.5;
    const close = 100 + trend + wave;
    const open = close - Math.cos(index / 4) * 1.8;
    const high = Math.max(open, close) + 2.4;
    const low = Math.min(open, close) - 2.4;
    return {
      time: start + index * intervalSeconds,
      open,
      high,
      low,
      close,
      volume: 1000 + index,
    };
  });
}

const fmtPct = (value) => `${(value * 100).toFixed(2)}%`;
const fmtPrice = (value) => `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtMatchScore = (distance) => `${(100 / (1 + distance)).toFixed(1)}%`;
const formatUpdatedAt = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleString(undefined, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};
const symbolLabel = (symbol) => SYMBOL_OPTIONS.find((option) => option.value === symbol)?.label || symbol;
const intervalLabel = (interval) => INTERVAL_OPTIONS.find((option) => option.value === interval)?.label || interval.toUpperCase();
const fmtAxisPrice = (value) => Number(value).toLocaleString(undefined, {
  maximumFractionDigits: value >= 1000 ? 0 : 2,
});

function StatCard({ label, value, tone }) {
  return (
    <div className="pm-stat-card">
      <span>{label}</span>
      <strong className={tone ? `pm-${tone}` : ''}>{value}</strong>
    </div>
  );
}

function DateTicks({ candles, xForIndex, y }) {
  if (!candles.length) return null;
  const mid = Math.floor((candles.length - 1) / 2);
  return [
    { index: 0, label: candles[0].date },
    { index: mid, label: candles[mid].date },
    { index: candles.length - 1, label: candles[candles.length - 1].date },
  ].map((tick) => (
    <text key={`${tick.index}-${tick.label}`} x={xForIndex(tick.index)} y={y} className="pm-chart-date">
      {tick.label}
    </text>
  ));
}

function SvgCandlestickChart({
  candles,
  forwardStartIndex = null,
  height = 180,
  matchLabel = '',
  forwardLabel = '',
}) {
  const { t } = useLang();
  const width = 720;
  const leftPadding = 68;
  const rightPadding = 18;
  const topPadding = 24;
  const bottomPadding = 24;
  const labelHeight = 18;
  const chartHeight = height - labelHeight;
  const highs = candles.map((candle) => candle.high);
  const lows = candles.map((candle) => candle.low);
  const max = Math.max(...highs);
  const min = Math.min(...lows);
  const range = max - min || 1;
  const chartWidth = width - leftPadding - rightPadding;
  const slot = chartWidth / candles.length;
  const bodyWidth = Math.max(4, slot * 0.52);
  const y = (price) => topPadding + ((max - price) / range) * (chartHeight - topPadding - bottomPadding);
  const xForIndex = (index) => leftPadding + index * slot + slot / 2;
  const priceTicks = [max, (max + min) / 2, min];

  return (
    <svg className="pm-candle-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t('patternMatch.candleChartAriaLabel')}>
      {forwardStartIndex !== null && (
        <>
          <rect
            x={leftPadding + forwardStartIndex * slot}
            y={0}
            width={width - rightPadding - (leftPadding + forwardStartIndex * slot)}
            height={chartHeight}
            className="pm-forward-zone"
          />
          <line
            x1={leftPadding + forwardStartIndex * slot}
            y1={0}
            x2={leftPadding + forwardStartIndex * slot}
            y2={chartHeight}
            className="pm-split-line"
          />
          <text x={leftPadding + (forwardStartIndex * slot) / 2} y={16} className="pm-zone-label">{matchLabel}</text>
          <text x={leftPadding + forwardStartIndex * slot + ((candles.length - forwardStartIndex) * slot) / 2} y={16} className="pm-zone-label">{forwardLabel}</text>
        </>
      )}
      {priceTicks.map((tick) => (
        <g key={tick}>
          <line x1={leftPadding} y1={y(tick)} x2={width - rightPadding} y2={y(tick)} className="pm-grid-line" />
          <text x={leftPadding - 8} y={y(tick) + 4} className="pm-axis-label">
            {fmtAxisPrice(tick)}
          </text>
        </g>
      ))}
      <line x1={leftPadding} y1={chartHeight - bottomPadding} x2={width - rightPadding} y2={chartHeight - bottomPadding} className="pm-axis" />
      {candles.map((candle, index) => {
        const x = xForIndex(index);
        const openY = y(candle.open);
        const closeY = y(candle.close);
        const top = Math.min(openY, closeY);
        const bodyHeight = Math.max(2, Math.abs(openY - closeY));
        const up = candle.close >= candle.open;
        return (
          <g key={`${candle.time}-${index}`} className={up ? 'pm-candle-up' : 'pm-candle-down'}>
            <line x1={x} y1={y(candle.high)} x2={x} y2={y(candle.low)} />
            <rect x={x - bodyWidth / 2} y={top} width={bodyWidth} height={bodyHeight} rx="1.5" />
          </g>
        );
      })}
      <DateTicks candles={candles} xForIndex={xForIndex} y={height - 5} />
    </svg>
  );
}

export function toLightweightCandles(candles) {
  return candles.map((candle) => ({
    time: candle.time,
    open: candle.open,
    high: candle.high,
    low: candle.low,
    close: candle.close,
  }));
}

function CandlestickChart(props) {
  const { candles, forwardStartIndex = null, height = 180, matchLabel = '', forwardLabel = '' } = props;
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const isTest = process.env.NODE_ENV === 'test';

  useEffect(() => {
    if (isTest || !containerRef.current || !candles.length) return undefined;

    const container = containerRef.current;
    let chart;
    let cancelled = false;
    let cleanup = () => {};

    import('lightweight-charts').then(({ CandlestickSeries, createChart }) => {
      if (cancelled || !containerRef.current) return;

      chart = createChart(container, {
      width: container.clientWidth || 720,
      height,
      layout: {
        background: { color: '#fffdf7' },
        textColor: '#8d7c56',
      },
      grid: {
        vertLines: { color: 'rgba(80, 70, 45, 0.12)' },
        horzLines: { color: 'rgba(80, 70, 45, 0.18)' },
      },
      rightPriceScale: {
        borderColor: 'rgba(80, 70, 45, 0.18)',
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      timeScale: {
        borderColor: 'rgba(80, 70, 45, 0.18)',
        timeVisible: false,
        secondsVisible: false,
      },
      crosshair: {
        mode: 0,
      },
      });

      const series = chart.addSeries(CandlestickSeries, {
      upColor: '#16a35b',
      downColor: '#ef2f2f',
      borderUpColor: '#16a35b',
      borderDownColor: '#ef2f2f',
      wickUpColor: '#16a35b',
      wickDownColor: '#ef2f2f',
      });

      series.setData(toLightweightCandles(candles));

      chart.timeScale().fitContent();

      const syncOverlay = () => {
        const overlay = overlayRef.current;
        if (!overlay || forwardStartIndex === null) return;
        const x = chart.timeScale().logicalToCoordinate(forwardStartIndex);
        if (x === null) return;
        const cw = container.clientWidth || 1;
        const pct = (x / cw) * 100;
        const zone = overlay.querySelector('.pm-lw-forward-zone');
        const line = overlay.querySelector('.pm-lw-split-line');
        const fwdLabel = overlay.querySelector('.pm-lw-zone-label.forward');
        const matLabel = overlay.querySelector('.pm-lw-zone-label.match');
        if (zone) zone.style.left = `${pct}%`;
        if (line) line.style.left = `${pct}%`;
        if (fwdLabel) fwdLabel.style.left = `${pct + (100 - pct) / 2}%`;
        if (matLabel) matLabel.style.left = `${pct / 2}%`;
      };

      chart.timeScale().subscribeVisibleLogicalRangeChange(syncOverlay);

      const handleResize = () => {
        chart.applyOptions({ width: container.clientWidth || 720 });
        chart.timeScale().fitContent();
      };

      window.addEventListener('resize', handleResize);
      cleanup = () => {
        window.removeEventListener('resize', handleResize);
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(syncOverlay);
        chart.remove();
      };
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [candles, height, isTest, forwardStartIndex]);

  if (isTest) {
    return <SvgCandlestickChart {...props} />;
  }

  const forwardPct = forwardStartIndex !== null
    ? Math.max(0, Math.min(100, (forwardStartIndex / candles.length) * 100))
    : null;

  return (
    <div className="pm-lw-chart-wrap" style={{ height }}>
      {forwardPct !== null && (
        <div ref={overlayRef} className="pm-lw-forward-layer" aria-hidden="true">
          <div className="pm-lw-forward-zone" style={{ left: `${forwardPct}%` }} />
          <div className="pm-lw-split-line" style={{ left: `${forwardPct}%` }} />
          <span className="pm-lw-zone-label match">{matchLabel}</span>
          <span className="pm-lw-zone-label forward" style={{ left: `${forwardPct + (100 - forwardPct) / 2}%` }}>{forwardLabel}</span>
        </div>
      )}
      <div ref={containerRef} className="pm-lw-chart" />
    </div>
  );
}

function OverlayChart({ currentPattern, matches, selectedRank }) {
  const { t } = useLang();
  const width = 720;
  const height = 230;
  const padding = 26;
  const leftPadding = 58;
  const rightPadding = 18;
  const series = [currentPattern, ...matches.map((match) => match.pattern)];
  const values = series.flat();
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const percentTicks = [max, (max + min) / 2, min];

  const linePath = (points) => points.map((value, index) => {
    const x = leftPadding + (index / (points.length - 1)) * (width - leftPadding - rightPadding);
    const y = padding + ((max - value) / range) * (height - padding * 2);
    return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');

  return (
    <svg className="pm-overlay-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t('patternMatch.overlayChartAriaLabel')}>
      {percentTicks.map((tick) => (
        <g key={tick}>
          <line
            x1={leftPadding}
            y1={padding + ((max - tick) / range) * (height - padding * 2)}
            x2={width - rightPadding}
            y2={padding + ((max - tick) / range) * (height - padding * 2)}
            className="pm-grid-line"
          />
          <text x={leftPadding - 8} y={padding + ((max - tick) / range) * (height - padding * 2) + 4} className="pm-axis-label">
            {fmtPct(tick)}
          </text>
        </g>
      ))}
      {matches.map((match, index) => (
        <path
          key={match.rank}
          d={linePath(match.pattern)}
          style={{ stroke: TOP_COLORS[index] }}
          className={match.rank === selectedRank ? 'pm-match-line active' : 'pm-match-line'}
        />
      ))}
      <path d={linePath(currentPattern)} className="pm-current-line" />
      <text x={leftPadding} y={height - 7} className="pm-chart-date">1</text>
      <text x={width / 2} y={height - 7} className="pm-chart-date">Bar</text>
      <text x={width - rightPadding} y={height - 7} className="pm-chart-date">{currentPattern.length}</text>
    </svg>
  );
}

function TopLegend({ currentSegment, matches }) {
  const { t } = useLang();
  return (
    <div className="pm-top-legend">
      <span>{t('patternMatch.currentLabel')}: {currentSegment[0].date} ~ {currentSegment.at(-1).date}</span>
      {matches.map((match, index) => (
        <span key={match.rank} style={{ color: TOP_COLORS[index] }}>
          Top{match.rank}: {match.startDate} ~ {match.endDate}
        </span>
      ))}
    </div>
  );
}

function MatchCard({ match, selected, onSelect }) {
  const { t } = useLang();
  const mergedCandles = [...match.candles, ...match.forwardCandles];
  const direction = match.forwardReturn >= 0 ? t('patternMatch.up') : t('patternMatch.down');

  return (
    <article className={selected ? 'pm-match-card active' : 'pm-match-card'}>
      <button type="button" className="pm-card-hit" aria-label={t('patternMatch.viewMatch', { rank: match.rank })} onClick={() => onSelect(match.rank)}>
        <div className="pm-card-head">
          <div className="pm-card-title">
            <span className="pm-rank-pill">#{match.rank}</span>
            <strong>{match.startDate} ~ {match.endDate}</strong>
          </div>
          <div className="pm-score-box">
            <span>{t('patternMatch.matchScore')}</span>
            <strong>{fmtMatchScore(match.distance)}</strong>
            <em className={match.forwardReturn >= 0 ? 'up' : 'down'}>{direction}</em>
          </div>
        </div>
        <CandlestickChart
          candles={mergedCandles}
          forwardStartIndex={match.candles.length}
          height={190}
          matchLabel={t('patternMatch.matchZone')}
          forwardLabel={t('patternMatch.forwardZone')}
        />
        <div className="pm-card-metrics">
          <span>{t('patternMatch.return')} <strong className={match.forwardReturn >= 0 ? 'pm-positive' : 'pm-negative'}>{fmtPct(match.forwardReturn)}</strong></span>
          <span>{t('patternMatch.maxDrawdown')} <strong className="pm-negative">{fmtPct(match.maxDrawdown)}</strong></span>
          <span>{t('patternMatch.maxRunup')} <strong className="pm-positive">{fmtPct(match.maxRunup)}</strong></span>
        </div>
      </button>
    </article>
  );
}

function SelectedMatchDetail({ match }) {
  const { t } = useLang();
  if (!match) return null;

  const mergedCandles = [...match.candles, ...match.forwardCandles];
  const direction = match.forwardReturn >= 0 ? t('patternMatch.up') : t('patternMatch.down');

  return (
    <section className="pm-panel pm-selected-detail">
      <div className="pm-section-title compact">
        <div>
          <h2>{t('patternMatch.selectedCaseTitle', { rank: match.rank })}</h2>
          <p>{t('patternMatch.selectedCaseDesc', { startDate: match.startDate, endDate: match.endDate, observationEndDate: match.observationEndDate })}</p>
        </div>
        <div className="pm-selected-score">
          <span>{t('patternMatch.matchScore')}</span>
          <strong>{fmtMatchScore(match.distance)}</strong>
          <em className={match.forwardReturn >= 0 ? 'up' : 'down'}>{direction}</em>
        </div>
      </div>
      <div className="pm-selected-grid">
        <div>
          <CandlestickChart
            candles={mergedCandles}
            forwardStartIndex={match.candles.length}
            height={290}
            matchLabel={t('patternMatch.matchZone')}
            forwardLabel={t('patternMatch.forwardZone')}
          />
        </div>
        <aside className="pm-selected-metrics">
          <div>
            <span>{t('patternMatch.forwardReturn')}</span>
            <strong className={match.forwardReturn >= 0 ? 'pm-positive' : 'pm-negative'}>{fmtPct(match.forwardReturn)}</strong>
          </div>
          <div>
            <span>{t('patternMatch.maxDrawdown')}</span>
            <strong className="pm-negative">{fmtPct(match.maxDrawdown)}</strong>
          </div>
          <div>
            <span>{t('patternMatch.maxRunup')}</span>
            <strong className="pm-positive">{fmtPct(match.maxRunup)}</strong>
          </div>
          <p>{t('patternMatch.caseDetailNote')}</p>
        </aside>
      </div>
    </section>
  );
}

function MetricNotes() {
  const { t } = useLang();
  return (
    <section className="pm-panel pm-notes">
      <h2>{t('patternMatch.dataNotesTitle')}</h2>
      <p><strong>{t('patternMatch.toolReminder')}</strong>{t('patternMatch.toolReminderDesc')}</p>
      <p><strong>{t('patternMatch.matchScore')}</strong>{t('patternMatch.matchScoreDesc')}</p>
      <p><strong>{t('patternMatch.matchZone')}</strong>{t('patternMatch.matchZoneDesc')}</p>
      <p><strong>{t('patternMatch.forwardZone')}</strong>{t('patternMatch.forwardZoneDesc')}</p>
      <p><strong>{t('patternMatch.return')}</strong>{t('patternMatch.returnDesc')}</p>
      <p><strong>{t('patternMatch.maxDrawdownRunup')}</strong>{t('patternMatch.maxDrawdownRunupDesc')}</p>
    </section>
  );
}


function PatternMatchPage() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOL);
  const [interval, setInterval] = useState(DEFAULT_INTERVAL);
  const [params, setParams] = useState(DEFAULT_PARAMS);
  const [candles, setCandles] = useState([]);
  const [dataset, setDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRank, setSelectedRank] = useState(1);
  const { isLoggedIn } = useUserStore();

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    fetch(`/api/market/history?symbol=${symbol}&interval=${interval}&limit=${HISTORY_LIMIT}`)
      .then((response) => {
        if (!response.ok) throw new Error(t('patternMatch.historyLoadError'));
        return response.json();
      })
      .then((payload) => {
        if (!mounted) return;
        const normalizedCandles = normalizeApiCandles(payload.candles, payload.interval || interval);
        if (!normalizedCandles.length || payload.source === 'unavailable') {
          console.warn('[PatternMatchPage] Market history unavailable', {
            symbol,
            interval,
            source: payload.source,
            error: payload.error,
          });
          const fallbackCandles = normalizeApiCandles(buildFallbackCandles(interval), interval);
          setCandles(fallbackCandles);
          setDataset({
            symbol: payload.symbol || symbol,
            interval: payload.interval || interval,
            source: 'local fallback',
            updatedAt: payload.updatedAt,
            fallbackFrom: payload.source,
          });
          setLoading(false);
          return;
        }
        setCandles(normalizedCandles);
        setDataset({
          symbol: payload.symbol || symbol,
          interval: payload.interval || interval,
          source: payload.source || 'unknown',
          updatedAt: payload.updatedAt,
          fallbackFrom: payload.fallbackFrom,
        });
        setLoading(false);
      })
      .catch((err) => {
        if (!mounted) return;
        console.warn('[PatternMatchPage] Market history unavailable', {
          symbol,
          interval,
          error: err.message,
        });
        const fallbackCandles = normalizeApiCandles(buildFallbackCandles(interval), interval);
        setCandles(fallbackCandles);
        setDataset({
          symbol,
          interval,
          source: 'local fallback',
          updatedAt: null,
          fallbackFrom: 'market history api',
        });
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [interval, symbol, t]);

  const computed = useMemo(() => {
    if (!candles.length) return null;
    const matches = findPatternMatches(candles, {
      ...params,
      matchCount: FIXED_MATCH_COUNT,
    });
    const currentStart = candles.length - params.patternLength;
    const currentSegment = candles.slice(currentStart);
    return {
      matches,
      summary: summarizeMatches(matches),
      currentSegment,
      currentPattern: normalizeCloses(currentSegment),
      latest: candles[candles.length - 1],
      first: candles[0],
    };
  }, [candles, params]);

  useEffect(() => {
    setSelectedRank(1);
  }, [params.patternLength, params.forwardWindow, params.exclusionWindow]);

  useEffect(() => {
    setSelectedRank(1);
  }, [symbol, interval]);

  const selectedMatch = computed?.matches.find((match) => match.rank === selectedRank) || computed?.matches[0];

  const handleParam = (key, value) => {
    setParams((current) => ({
      ...current,
      [key]: Math.max(1, Number(value) || current[key]),
    }));
  };

  const handlePremiumSelect = (key, rawValue) => {
    const value = Number(rawValue);
    if (!isLoggedIn && PREMIUM_OPTIONS.includes(value)) {
      Swal.fire({
        title: '登录后使用',
        text: '30 / 40 / 50 档位需要登录后才能使用。',
        confirmButtonText: '去登录',
        cancelButtonText: '取消',
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) navigate(ROUTES.login);
      });
      return;
    }
    handleParam(key, value);
  };

  if (loading) {
    return <main className="pattern-page"><div className="pm-state">{t('patternMatch.loading')}</div></main>;
  }

  if (!computed) {
    console.warn('[PatternMatchPage] Pattern computation unavailable', { symbol, interval });
    return <main className="pattern-page"><div className="pm-state">{t('patternMatch.noCandleData')}</div></main>;
  }

  const currentReturn = computed.currentSegment.at(-1).close / computed.currentSegment[0].close - 1;

  return (
    <main className="pattern-page">
      <header className="pm-tool-hero">
        <button type="button" className="pm-back-link" onClick={() => navigate(ROUTES.market)}>← 返回市场</button>
        <p className="pm-kicker">Pattern Match</p>
        <h1>{t('patternMatch.pageTitle')}</h1>
        <p>
          {t('patternMatch.pageDesc')}
        </p>
        <div className="pm-hero-meta" aria-label="Pattern match dataset">
          <span>{symbolLabel(dataset?.symbol || symbol)}</span>
          <span>{intervalLabel(dataset?.interval || interval)}</span>
          <span>Top 5</span>
          <span>{computed.first.date} ~ {computed.latest.date}</span>
          <span>{dataset?.source || 'unknown'}</span>
          <span>{t('patternMatch.updatedAt')} {formatUpdatedAt(dataset?.updatedAt)}</span>
        </div>
      </header>

      <section className="pm-panel pm-params">
        <label>
          <span>{t('patternMatch.symbolLabel')}</span>
          <select aria-label="Symbol" value={symbol} onChange={(event) => setSymbol(event.target.value)}>
            {SYMBOL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span>{t('patternMatch.intervalLabel')}</span>
          <select aria-label="Interval" value={interval} onChange={(event) => setInterval(event.target.value)}>
            {INTERVAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
        <div className="pm-param-group">
          <span>{t('patternMatch.patternLengthLabel')}</span>
          <div className="pm-length-options" aria-label={t('patternMatch.patternLengthLabel')}>
            {BASIC_OPTIONS.map((length) => (
              <button
                key={length}
                type="button"
                className={params.patternLength === length ? 'active' : ''}
                onClick={() => handleParam('patternLength', length)}
              >
                {length}
              </button>
            ))}
            <select
              className="pm-length-select"
              value={PREMIUM_OPTIONS.includes(params.patternLength) ? params.patternLength : ''}
              aria-label="更多档位"
              onChange={(event) => handlePremiumSelect('patternLength', event.target.value)}
            >
              <option value="" disabled>更多</option>
              {PREMIUM_OPTIONS.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="pm-param-group">
          <span>{t('patternMatch.forwardWindowLabel')}</span>
          <div className="pm-length-options" aria-label={t('patternMatch.forwardWindowLabel')}>
            {BASIC_OPTIONS.map((window) => (
              <button
                key={window}
                type="button"
                className={params.forwardWindow === window ? 'active' : ''}
                onClick={() => handleParam('forwardWindow', window)}
              >
                {window}
              </button>
            ))}
            <select
              className="pm-length-select"
              value={PREMIUM_OPTIONS.includes(params.forwardWindow) ? params.forwardWindow : ''}
              aria-label="更多档位"
              onChange={(event) => handlePremiumSelect('forwardWindow', event.target.value)}
            >
              <option value="" disabled>更多</option>
              {PREMIUM_OPTIONS.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="pm-param-group">
          <span>{t('patternMatch.exclusionWindowLabel')}</span>
          <div className="pm-length-options" aria-label={t('patternMatch.exclusionWindowLabel')}>
            {BASIC_OPTIONS.map((value) => (
              <button
                key={value}
                type="button"
                className={params.exclusionWindow === value ? 'active' : ''}
                onClick={() => handleParam('exclusionWindow', value)}
              >
                {value}
              </button>
            ))}
            <select
              className="pm-length-select"
              value={PREMIUM_OPTIONS.includes(params.exclusionWindow) ? params.exclusionWindow : ''}
              aria-label="更多档位"
              onChange={(event) => handlePremiumSelect('exclusionWindow', event.target.value)}
            >
              <option value="" disabled>更多</option>
              {PREMIUM_OPTIONS.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="pm-fixed-param">
          <span>{t('patternMatch.matchCount')}</span>
          <strong>Top 5</strong>
        </div>
      </section>

      <section className="pm-summary-grid">
        <StatCard label={t('patternMatch.top5UpRatio')} value={fmtPct(computed.summary.upProbability)} tone={computed.summary.upProbability >= 0.5 ? 'positive' : 'negative'} />
        <StatCard label={t('patternMatch.avgForwardReturn')} value={fmtPct(computed.summary.averageReturn)} tone={computed.summary.averageReturn >= 0 ? 'positive' : 'negative'} />
        <StatCard label={t('patternMatch.medianReturn')} value={fmtPct(computed.summary.medianReturn)} tone={computed.summary.medianReturn >= 0 ? 'positive' : 'negative'} />
        <StatCard label={t('patternMatch.avgMaxDrawdown')} value={fmtPct(computed.summary.averageMaxDrawdown)} tone="negative" />
      </section>

      <section className="pm-panel pm-overview">
        <div className="pm-section-title compact">
          <div>
            <h2>{t('patternMatch.currentVsTop5')}</h2>
            <p>{t('patternMatch.currentVsTop5Desc')}</p>
          </div>
          <div className="pm-mini-metrics">
            <span>{t('patternMatch.latestClose')} {fmtPrice(computed.latest.close)}</span>
            <strong className={currentReturn >= 0 ? 'pm-positive' : 'pm-negative'}>{params.patternLength}{t('patternMatch.barUnit')} {fmtPct(currentReturn)}</strong>
          </div>
        </div>
        <div className="pm-overview-grid">
          <div>
            <div className="pm-chart-label">{t('patternMatch.currentCandleTrend')}</div>
            <CandlestickChart candles={computed.currentSegment} height={230} />
          </div>
          <div>
            <div className="pm-chart-label">{t('patternMatch.barNormalizedSimilarity')}</div>
            <OverlayChart currentPattern={computed.currentPattern} matches={computed.matches} selectedRank={selectedMatch?.rank} />
          </div>
        </div>
        <TopLegend currentSegment={computed.currentSegment} matches={computed.matches} />
      </section>

      <section className="pm-top-section">
        <div className="pm-section-heading">
          <h2>{t('patternMatch.top5DetailTitle')} <span data-testid="selected-match-rank">#{selectedMatch?.rank}</span></h2>
          <p>{t('patternMatch.top5DetailDesc')}</p>
        </div>
        <div className="pm-card-grid">
          {computed.matches.map((match) => (
            <MatchCard
              key={match.rank}
              match={match}
              selected={selectedMatch?.rank === match.rank}
              onSelect={setSelectedRank}
            />
          ))}
        </div>
      </section>

      <SelectedMatchDetail match={selectedMatch} />

      <MetricNotes />

      <RebateBanner />
    </main>
  );
}

export default PatternMatchPage;
